/** Jackson sends Long keys as strings in nested maps. */
export function numericRecord(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const id = Number(k);
    if (!Number.isFinite(id)) continue;
    const n = Number(v);
    if (Number.isFinite(n)) out[id] = n;
  }
  return Object.keys(out).length ? out : null;
}

/** Mirrors odos_client RouteReadyScreen helpers (same backend RouteCostPreviewDTO). */
export function routeServiceFeeFromPreview(p) {
  const fromBackend = Math.max(0, Number(p.serviceFeeEtb ?? 0));
  if (fromBackend > 0) return fromBackend;
  const te = Number(p.totalEarning ?? 0);
  const pct = Number(p.serviceFeePercent ?? 20);
  return Math.max(0, te * (pct / 100));
}

export function driverDirectPaymentFromPreview(p) {
  const fromBackend = Math.max(0, Number(p.directPaymentToDriverEtb ?? 0));
  if (fromBackend > 0) return fromBackend;
  const te = Number(p.totalEarning ?? 0);
  return Math.max(0, te - routeServiceFeeFromPreview(p));
}

export function clientSavingsShareFromPreview(p) {
  const ta = Number(p.totalAloneCost ?? 0);
  const te = Number(p.totalEarning ?? 0);
  return Math.max(0, ta - te);
}

export function totalSavedVsSeparateFromPreview(p) {
  const ts = Number(p.totalSavings);
  if (Number.isFinite(ts)) return Math.max(0, ts);
  return clientSavingsShareFromPreview(p);
}

/**
 * Sum of per-stop (alone − batch slice via actualStopCostFromPreview).
 */
export function totalSavedMatchingPerStopRows(p, aloneMap, actualMap, orderedIds) {
  if (!orderedIds || orderedIds.length <= 1 || !aloneMap) {
    return totalSavedVsSeparateFromPreview(p);
  }
  let sum = 0;
  let anyAlone = false;
  for (const orderId of orderedIds) {
    const aloneVal = aloneMap[orderId];
    if (aloneVal == null) continue;
    anyAlone = true;
    const batchVal = actualStopCostFromPreview(p, aloneMap, actualMap, orderId);
    sum += Math.max(0, aloneVal - batchVal);
  }
  if (!anyAlone) return totalSavedVsSeparateFromPreview(p);
  return sum;
}

export function displayRouteBonusFromPreview(p) {
  const te = Number(p.totalEarning ?? 0);
  const be = Number(p.baseEarning ?? 0);
  return Math.max(0, te - be);
}

export function actualStopCostFromPreview(p, aloneMap, actualMap, orderId) {
  const exact = actualMap?.[orderId];
  if (exact != null) return Math.max(0, exact);
  const alone = aloneMap?.[orderId];
  const totalAlone = Number(p.totalAloneCost ?? 0);
  if (alone == null || totalAlone <= 0) return 0;
  const ratio = Math.min(1, Math.max(0, alone / totalAlone));
  return Math.max(0, driverDirectPaymentFromPreview(p) * ratio);
}

export function formatVehicleLabel(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  return s
    .toLowerCase()
    .split('_')
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');
}

export function buildCostPreviewDerived(costPreview, orderedOrderIds) {
  if (!costPreview) return null;
  const p = costPreview;
  const alone = numericRecord(p.aloneCostsPerStop);
  const actual = numericRecord(p.actualCostsPerStop);
  const ids = orderedOrderIds ?? [];
  return {
    alone,
    actual,
    dir: driverDirectPaymentFromPreview(p),
    svc: routeServiceFeeFromPreview(p),
    /** Alone vs allocated batch slice per stop (teasers / “Savings” card); not the same as `bonus`. */
    save: totalSavedMatchingPerStopRows(p, alone, actual, ids),
    /** `totalEarning − baseEarning`- batch premium shown as “You save” next to route earnings. */
    bonus: displayRouteBonusFromPreview(p),
    totalAlone: Number(p.totalAloneCost ?? 0),
    totalYouPay: Number(p.totalEarning ?? 0),
    /** Fleet distance rate (same as vehiclePricePerKm from API) */
    pricePerKm: Number(p.pricePerKm ?? 0),
    /** Fleet-only rate from system_default {@code price_per_km_{VEHICLE}} */
    vehiclePricePerKm: (() => {
      const v = Number(p.vehiclePricePerKm);
      if (Number.isFinite(v) && v >= 0) return v;
      return Number(p.pricePerKm ?? 0);
    })(),
  };
}
